import { useEffect, useRef } from "react";
import DOMPurify from "dompurify";
import { Bold, Italic, Underline, Heading2, Heading3, List, ListOrdered, RemoveFormatting } from "lucide-react";

const ALLOWED = {
  ALLOWED_TAGS: ["p", "br", "b", "strong", "i", "em", "u", "ul", "ol", "li", "h2", "h3", "h4", "a", "span", "div"],
  ALLOWED_ATTR: ["href", "target", "rel"],
};

const hasHtmlTags = (s) => /<[a-z!/][\s\S]*>/i.test(s || "");

// Legacy plain text (no tags) is escaped and its line breaks kept, so existing
// content looks exactly the same after switching a field to rich text.
export const prepareHtml = (v) => {
  const s = v || "";
  if (hasHtmlTags(s)) return s;
  return s
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/\n/g, "<br>");
};

export const cleanHtml = (html) => DOMPurify.sanitize(prepareHtml(html), ALLOWED);

export const richToPlain = (html) =>
  (html || "").replace(/<br\s*\/?>/gi, " ").replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").replace(/&amp;/g, "&").replace(/&lt;/g, "<").replace(/&gt;/g, ">").trim();

// Normalise HTML pasted from Word / Google Docs so its formatting survives sanitisation.
// Docs wraps the whole selection in <b style="font-weight:normal"> and expresses bold/
// italic/underline via inline styles — stripping styles alone would make everything bold.
const BOLD_RE = /font-weight:\s*(bold|bolder|[6-9]00)/i;
const NORMAL_WEIGHT_RE = /font-weight:\s*(normal|lighter|[1-5]00)/i;
const ITALIC_RE = /font-style:\s*italic/i;
const UNDERLINE_RE = /text-decoration[^;"']*underline/i;

export const normalizePastedHtml = (raw) => {
  if (!raw) return "";
  const root = document.createElement("div");
  root.innerHTML = raw;

  // Map unsupported headings to supported ones so pasted headings are kept.
  root.querySelectorAll("h1").forEach((el) => renameEl(el, "h2"));
  root.querySelectorAll("h5,h6").forEach((el) => renameEl(el, "h4"));

  root.querySelectorAll("*").forEach((el) => {
    if (!root.contains(el)) return;
    const style = el.getAttribute("style") || "";
    const tag = el.tagName;

    // 1) Unwrap fake-bold wrappers (e.g. Google Docs <b style="font-weight:normal">).
    if ((tag === "B" || tag === "STRONG") && NORMAL_WEIGHT_RE.test(style)) {
      unwrap(el);
      return;
    }

    // 2) Convert inline-styled formatting into real semantic tags before styles are stripped.
    if (style) {
      const wantBold = BOLD_RE.test(style);
      const wantItalic = ITALIC_RE.test(style);
      const wantUnderline = UNDERLINE_RE.test(style);
      if (wantBold || wantItalic || wantUnderline) {
        const frag = document.createDocumentFragment();
        while (el.firstChild) frag.appendChild(el.firstChild);
        let content = frag;
        if (wantUnderline) content = wrapNode("u", content);
        if (wantItalic) content = wrapNode("em", content);
        if (wantBold) content = wrapNode("strong", content);
        el.appendChild(content);
      }
    }
  });

  return root.innerHTML;
};

function unwrap(el) {
  const parent = el.parentNode;
  if (!parent) return;
  while (el.firstChild) parent.insertBefore(el.firstChild, el);
  parent.removeChild(el);
}

function wrapNode(tagName, node) {
  const w = document.createElement(tagName);
  w.appendChild(node);
  return w;
}

function renameEl(el, tagName) {
  const n = document.createElement(tagName);
  while (el.firstChild) n.appendChild(el.firstChild);
  el.parentNode?.replaceChild(n, el);
}

export const isRichEmpty = (html) => {
  const txt = (html || "").replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").trim();
  return txt.length === 0;
};

const proseClasses =
  "[&_h2]:text-xl [&_h2]:font-bold [&_h2]:font-heading [&_h2]:mt-4 [&_h2]:mb-2 " +
  "[&_h3]:text-lg [&_h3]:font-semibold [&_h3]:font-heading [&_h3]:mt-3 [&_h3]:mb-1.5 " +
  "[&_p]:my-2 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:my-2 [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:my-2 " +
  "[&_li]:my-1 [&_a]:text-[#1E63C4] [&_a]:underline [&_strong]:font-semibold leading-relaxed";

// Read-only renderer for stored rich text
export function RichText({ html, className = "", testid }) {
  return (
    <div
      data-testid={testid}
      className={`${proseClasses} text-[#333333] ${className}`}
      dangerouslySetInnerHTML={{ __html: cleanHtml(html) }}
    />
  );
}

const ToolbarBtn = ({ onClick, title, testid, children }) => (
  <button
    type="button"
    onMouseDown={(e) => e.preventDefault()}
    onClick={onClick}
    title={title}
    data-testid={testid}
    className="h-8 w-8 inline-flex items-center justify-center rounded text-[#555555] hover:bg-[#1E63C4]/10 hover:text-[#1E63C4] transition-colors"
  >
    {children}
  </button>
);

// contentEditable rich text editor (React 19 safe, uncontrolled — pass a `key` per record to reset)
export function RichTextEditor({ value, onChange, testid, placeholder = "Type or paste the review here…" }) {
  const ref = useRef(null);

  useEffect(() => {
    if (ref.current) ref.current.innerHTML = cleanHtml(value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const emit = () => {
    if (ref.current) onChange(cleanHtml(ref.current.innerHTML));
  };

  const exec = (cmd, arg) => {
    ref.current?.focus();
    try { document.execCommand("styleWithCSS", false, false); } catch {}
    document.execCommand(cmd, false, arg);
    emit();
  };

  const onPaste = (e) => {
    e.preventDefault();
    const html = e.clipboardData.getData("text/html");
    const text = e.clipboardData.getData("text/plain");
    const toInsert = html ? cleanHtml(normalizePastedHtml(html)) : (text || "").replace(/\n/g, "<br>");
    document.execCommand("insertHTML", false, toInsert);
    emit();
  };

  return (
    <div className="rounded-md border border-[#E5E7EB] focus-within:ring-2 focus-within:ring-[#1E63C4]" data-testid={testid ? `${testid}-wrapper` : undefined}>
      <div className="flex flex-wrap items-center gap-0.5 border-b border-[#E5E7EB] px-2 py-1 bg-[#F9FAF8] rounded-t-md">
        <ToolbarBtn onClick={() => exec("bold")} title="Bold" testid={testid ? `${testid}-bold` : undefined}><Bold className="h-4 w-4" /></ToolbarBtn>
        <ToolbarBtn onClick={() => exec("italic")} title="Italic" testid={testid ? `${testid}-italic` : undefined}><Italic className="h-4 w-4" /></ToolbarBtn>
        <ToolbarBtn onClick={() => exec("underline")} title="Underline" testid={testid ? `${testid}-underline` : undefined}><Underline className="h-4 w-4" /></ToolbarBtn>
        <span className="w-px h-5 bg-[#E5E7EB] mx-1" />
        <ToolbarBtn onClick={() => exec("formatBlock", "h2")} title="Heading" testid={testid ? `${testid}-h2` : undefined}><Heading2 className="h-4 w-4" /></ToolbarBtn>
        <ToolbarBtn onClick={() => exec("formatBlock", "h3")} title="Subheading" testid={testid ? `${testid}-h3` : undefined}><Heading3 className="h-4 w-4" /></ToolbarBtn>
        <span className="w-px h-5 bg-[#E5E7EB] mx-1" />
        <ToolbarBtn onClick={() => exec("insertUnorderedList")} title="Bullet list" testid={testid ? `${testid}-ul` : undefined}><List className="h-4 w-4" /></ToolbarBtn>
        <ToolbarBtn onClick={() => exec("insertOrderedList")} title="Numbered list" testid={testid ? `${testid}-ol` : undefined}><ListOrdered className="h-4 w-4" /></ToolbarBtn>
        <span className="w-px h-5 bg-[#E5E7EB] mx-1" />
        <ToolbarBtn onClick={() => { exec("removeFormat"); exec("formatBlock", "p"); }} title="Clear formatting" testid={testid ? `${testid}-clear` : undefined}><RemoveFormatting className="h-4 w-4" /></ToolbarBtn>
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={emit}
        onBlur={emit}
        onPaste={onPaste}
        data-testid={testid}
        data-placeholder={placeholder}
        className={`min-h-[160px] max-h-[420px] overflow-y-auto px-3 py-2.5 text-sm outline-none ${proseClasses} text-[#333333] empty:before:content-[attr(data-placeholder)] empty:before:text-[#999999]`}
      />
    </div>
  );
}
